import * as m from "@paraglide/messages"
import {
  PlaydataButtonAnchor,
  PlaydataFigure,
  PlaydataHeading,
  PlaydataSection,
} from "@playdata/PlaydataPrimitives"
import PlaydataReportGallery from "@playdata/PlaydataReportGallery"
import { PlaydataSpeechBubbleList } from "@playdata/PlaydataSpeechBubble"
import { usePlaydataSampleReportUrl } from "@playdata/playdata-settings-context"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"
import { FileBarChart2, Gamepad2 } from "lucide-react"

export const Route = createFileRoute("/$locale/playdata/event-report")({
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_eventReport_meta_description({}, { locale }),
      locale,
      path: "/playdata/event-report",
      title: m.playdata_eventReport_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)

  const pains = [
    m.playdata_eventReport_pain_item1({}, { locale }),
    m.playdata_eventReport_pain_item4({}, { locale }),
    m.playdata_eventReport_pain_item2({}, { locale }),
    m.playdata_eventReport_pain_item5({}, { locale }),
    m.playdata_eventReport_pain_item3({}, { locale }),
    m.playdata_eventReport_pain_item6({}, { locale }),
  ]

  return (
    <>
      <PlaydataSection className="py-[2.4rem]">
        <h1 className="text-center text-[2.2rem] md:text-[2.4rem]">
          <strong>{m.playdata_eventReport_heading({}, { locale })}</strong>
        </h1>
      </PlaydataSection>

      <PlaydataSection tone="light">
        <div className="grid items-center gap-[4rem] md:grid-cols-2">
          <PlaydataFigure
            alt=""
            height={900}
            src="/images/playdata/esports-tournament.jpg"
            width={1600}
          />
          <div className="flex flex-col items-start gap-[2rem]">
            <h2 className="text-[2.2rem] leading-[1.4] md:text-[2.6rem]">
              <strong>{m.playdata_eventReport_lead({}, { locale })}</strong>
            </h2>
            <p className="text-[1.5rem] leading-[1.7]">{m.playdata_eventReport_body({}, { locale })}</p>
            <PlaydataButtonAnchor
              className="self-stretch"
              href={sampleReportUrl}
              rel="noopener"
              target="_blank"
            >
              {m.playdata_common_sampleCta({}, { locale })}
            </PlaydataButtonAnchor>
          </div>
        </div>
      </PlaydataSection>

      <PlaydataSection>
        <PlaydataHeading
          className="mb-[1.6rem]"
          icon={Gamepad2}
          size="sub"
        >
          {m.playdata_eventReport_pain_heading({}, { locale })}
        </PlaydataHeading>
        <h3 className="mb-[4rem] text-center text-[2rem]">
          <strong>{m.playdata_eventReport_pain_intro({}, { locale })}</strong>
        </h3>
        <PlaydataSpeechBubbleList items={pains} />
      </PlaydataSection>

      <PlaydataSection className="pt-[2rem]">
        <PlaydataHeading
          className="mb-[4rem]"
          icon={FileBarChart2}
          size="sub"
        >
          <strong>{m.playdata_eventReport_example_heading({}, { locale })}</strong>
        </PlaydataHeading>
        <PlaydataReportGallery locale={locale} />
        <div className="mt-[4rem] flex flex-col items-center gap-[2.4rem] text-center">
          <p className="max-w-[60ch] text-[1.5rem] leading-[1.7]">
            {m.playdata_eventReport_sampleNote({}, { locale })}
          </p>
          <PlaydataButtonAnchor
            href={sampleReportUrl}
            rel="noopener"
            target="_blank"
          >
            {m.playdata_common_sampleCta({}, { locale })}
          </PlaydataButtonAnchor>
        </div>
      </PlaydataSection>
    </>
  )
}
